import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, Plus, Trash2, Loader2, Settings as SettingsIcon, ListOrdered } from 'lucide-react'
import { z } from 'zod'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Toggle } from '@/components/ui/Toggle'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { NumberInput } from '@/components/ui/NumberInput'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { PercentInput } from '@/components/ui/PercentInput'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { useZodForm } from '@/hooks/useZodForm'
import { useAuthority } from '@/hooks/useAuthority'
import { applyApiErrorsToForm } from '@/lib/api-errors'
import { onValidationFail } from '@/lib/form-helpers'
import {
  CommissionStructuresService,
  type CommissionStructure, type CommissionStructureRule,
  type CommissionStructuresApi,
} from '@/services/modules/CommissionStructuresService'
import { toast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'

export interface CommissionFormConfig {
  service: CommissionStructuresApi
  subtitle: string
  listPath: string
  crumbs: (edit: boolean) => { label: string; to?: string }[]
  /**
   * Permissões (authority) das rotas de escrita. Só o admin tem: as rotas do
   * parceiro (`/partner/...`) não usam authority e o `useAuthority` nunca
   * concede nada a parceiro — sem chave, a ação fica liberada.
   */
  authority?: { create: string; edit: string; rules: string }
}

const ADMIN_FORM_CFG: CommissionFormConfig = {
  service: CommissionStructuresService,
  subtitle: 'Comissionamento comercial',
  listPath: '/settings/commercial/commission-structures',
  crumbs: (edit) => [
    { label: 'Configurações', to: '/settings' },
    { label: 'Comercial' },
    { label: 'Comissionamento', to: '/settings/commercial/commission-structures' },
    { label: edit ? 'Editar' : 'Novo' },
  ],
  authority: {
    create: 'commercial.commission_structures.create',
    edit:   'commercial.commission_structures.edit',
    rules:  'commercial.commission_structures.rules',
  },
}

// =============================================================================
// Schema principal (zod)
// =============================================================================
const CARGOS = ['DIRETOR', 'SUPERVISOR', 'AFILIADO', 'VENDEDOR'] as const
const CARGO_LABELS: Record<string, string> = { DIRETOR: 'Diretor', SUPERVISOR: 'Supervisor', AFILIADO: 'Afiliado', VENDEDOR: 'Vendedor' }

const formSchema = z.object({
  name: z.string().trim().min(1, 'Informe um nome para o modelo de comissão.').max(150, 'O nome não pode passar de 150 caracteres.'),
  cargo: z.enum(CARGOS),
  noInstallmentsLimit: z.boolean(),
  installments: z.number().int().min(1).max(60).nullable(),
  noMeta: z.boolean(),
  meta: z.number().int().min(0).nullable(),
  status: z.enum(['ATIVO', 'INATIVO']),
}).superRefine((val, ctx) => {
  if (!val.noInstallmentsLimit && (val.installments === null || val.installments < 1)) {
    ctx.addIssue({ code: 'custom', path: ['installments'], message: 'Informe a quantidade de parcelas ou marque "Sem meta" para deixar livre.' })
  }
  if (!val.noMeta && (val.meta === null || val.meta < 0)) {
    ctx.addIssue({ code: 'custom', path: ['meta'], message: 'Informe a meta de vendas ou marque "Sem meta" para deixar livre.' })
  }
})
type FormValues = z.infer<typeof formSchema>

// =============================================================================
// Schema da regra (form inline) — mensagens em linguagem amigável
// =============================================================================
const ruleSchema = z.object({
  installmentAction: z.enum(['INTERVALO', 'A PARTIR DE']),
  installmentFrom: z.number({ message: 'Informe a parcela inicial.' }).int().min(1, 'A parcela inicial deve ser pelo menos 1.').max(60, 'A parcela inicial não pode ser maior que 60.'),
  installmentTo: z.number().int().min(1).max(60).nullable(),
  commissionType: z.enum(['VALOR FIXO', 'PORCENTAGEM']),
  value: z.number({ message: 'Informe o valor da comissão.' }).min(0, 'O valor da comissão não pode ser negativo.'),
}).superRefine((val, ctx) => {
  if (val.installmentAction === 'INTERVALO') {
    if (val.installmentTo === null || val.installmentTo === undefined) {
      ctx.addIssue({ code: 'custom', path: ['installmentTo'], message: 'Informe a parcela final do intervalo.' })
    } else if (val.installmentTo < val.installmentFrom) {
      ctx.addIssue({ code: 'custom', path: ['installmentTo'], message: 'A parcela final deve ser igual ou maior que a parcela inicial.' })
    }
  }
  if (val.commissionType === 'PORCENTAGEM' && val.value > 100) {
    ctx.addIssue({ code: 'custom', path: ['value'], message: 'A porcentagem não pode ser maior que 100%.' })
  }
})
type RuleValues = z.infer<typeof ruleSchema>

// =============================================================================
// Draft local (a regra existe antes de ser persistida no /novo)
// =============================================================================
interface DraftRule extends RuleValues {
  draftId: string
  id?: string // id real do backend quando persistida
}

// =============================================================================
// Page
// =============================================================================
export function CommissionStructureFormPage({ cfg = ADMIN_FORM_CFG }: { cfg?: CommissionFormConfig } = {}) {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id

  const [loading, setLoading] = useState(isEdit)
  const [activeSection, setActiveSection] = useState('basic')

  const { can } = useAuthority()
  const auth = cfg.authority
  // Regras (adicionar/remover/atualizar) têm permissão própria. O Salvar também
  // grava regras: cria todas no novo e propaga o cargo para elas na edição.
  const canManageRules = !auth || can(auth.rules)
  const canSave = (!auth || can(isEdit ? auth.edit : auth.create)) && canManageRules

  // Form principal — REGRA GLOBAL: defaults sem-meta=true (campos vazios).
  const form = useZodForm(formSchema, {
    defaultValues: {
      name: '',
      cargo: 'VENDEDOR',
      noInstallmentsLimit: true,
      installments: null,
      noMeta: true,
      meta: null,
      status: 'ATIVO',
    },
  })
  const noInstallmentsLimit = form.watch('noInstallmentsLimit')
  const noMeta              = form.watch('noMeta')

  // ----- Regras (state local) -----
  const [rules, setRules] = useState<DraftRule[]>([])
  const [rulesError, setRulesError] = useState<string | null>(null)
  const [deletingRule, setDeletingRule] = useState<DraftRule | null>(null)

  // Form de adição de regra
  const ruleForm = useForm<RuleValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(ruleSchema),
    defaultValues: {
      installmentAction: 'INTERVALO',
      installmentFrom: 1,
      installmentTo: 1,
      commissionType: 'PORCENTAGEM',
      value: 0,
    },
  })
  const ruleAction = ruleForm.watch('installmentAction')
  const ruleCommType = ruleForm.watch('commissionType')

  // ----- Carga em modo edição -----
  useEffect(() => {
    if (!isEdit || !id) return
    setLoading(true)
    Promise.all([
      cfg.service.show(id),
      cfg.service.listRules(id).catch(() => [] as CommissionStructureRule[]),
    ])
      .then(([s, list]) => {
        // Cargo da estrutura = cargo das regras (todas compartilham o mesmo).
        const cargo = (CARGOS as readonly string[]).includes(list[0]?.userType ?? '')
          ? (list[0]!.userType as FormValues['cargo']) : 'VENDEDOR'
        form.reset({
          name: s.name,
          cargo,
          noInstallmentsLimit: s.monthlyInstallments == null,
          installments: s.monthlyInstallments ?? null,
          noMeta: s.monthlyPlansNeedSold == null,
          meta: s.monthlyPlansNeedSold ?? null,
          status: s.status,
        })
        setRules(
          list
            .map((r): DraftRule => ({
              draftId: r.id,
              id: r.id,
              installmentFrom: r.installmentFrom,
              installmentTo: r.installmentTo,
              installmentAction: r.installmentAction,
              value: r.value,
              commissionType: r.commissionType,
            }))
            .sort((a, b) => a.installmentFrom - b.installmentFrom),
        )
      })
      .catch(() => toast.error('Tabela não encontrada.'))
      .finally(() => setLoading(false))
  }, [isEdit, id, form])

  // Quando "Sem meta" liga, zera valor da contraparte
  useEffect(() => {
    if (noInstallmentsLimit) form.setValue('installments', null, { shouldValidate: false })
  }, [noInstallmentsLimit, form])
  useEffect(() => {
    if (noMeta) form.setValue('meta', null, { shouldValidate: false })
  }, [noMeta, form])
  // Quando muda para "A PARTIR DE", limpa Até.
  useEffect(() => {
    if (ruleAction === 'A PARTIR DE') ruleForm.setValue('installmentTo', null, { shouldValidate: false })
  }, [ruleAction, ruleForm])

  // ----- Regras: helpers -----
  const sortedRules = useMemo(
    () => [...rules].sort((a, b) => a.installmentFrom - b.installmentFrom),
    [rules],
  )

  async function onAddRule(values: RuleValues) {
    const draft: DraftRule = {
      ...values,
      draftId: crypto.randomUUID(),
      installmentTo: values.installmentAction === 'A PARTIR DE' ? null : values.installmentTo,
    }

    // Validação de sobreposição com regras existentes.
    // "A PARTIR DE N" é tratado como faixa [N, ∞). Intervalos [a, b] sobrepõem
    // se a.from ≤ b.to && b.from ≤ a.to.
    const overlap = findOverlap(draft, rules)
    if (overlap) {
      const conflictDesc = describeRangeInstallment(overlap)
      ruleForm.setError('installmentFrom', {
        type: 'overlap',
        message: `Sobrepõe a regra existente: ${conflictDesc}.`,
      })
      if (draft.installmentAction === 'INTERVALO') {
        ruleForm.setError('installmentTo', {
          type: 'overlap',
          message: `Sobrepõe a regra existente: ${conflictDesc}.`,
        })
      }
      toast.error({
        title: 'Faixa de parcelas em conflito',
        description: `Essa nova regra sobrepõe uma regra já cadastrada (${conflictDesc}). Ajuste o intervalo para não se sobrepor.`,
      })
      return
    }

    if (isEdit && id) {
      try {
        const r = await cfg.service.createRule(id, {
          installmentFrom: draft.installmentFrom,
          installmentTo: draft.installmentTo,
          installmentAction: draft.installmentAction,
          value: draft.value,
          commissionType: draft.commissionType,
          userType: form.getValues('cargo'),
        } as Omit<CommissionStructureRule, 'id' | 'commissionStructureId' | 'formatted'>)
        draft.id = r.id
      } catch (e) {
        applyApiErrorsToForm(e, ruleForm.setError)
        return
      }
    }
    setRules((prev) => [...prev, draft])
    setRulesError(null)
    // Reseta apenas o valor (mantém ação/cargo/tipo pra agilidade)
    ruleForm.setValue('value', 0)
    ruleForm.clearErrors()
    toast.success({ title: 'Regra adicionada', description: 'A nova regra foi incluída no modelo.' })
  }

  async function confirmDelRule() {
    if (!deletingRule) return
    if (isEdit && id && deletingRule.id) {
      try {
        await cfg.service.destroyRule(id, deletingRule.id)
      } catch (e) {
        applyApiErrorsToForm(e, ruleForm.setError)
        return
      }
    }
    setRules((prev) => prev.filter((x) => x.draftId !== deletingRule.draftId))
    setDeletingRule(null)
    toast.success({ title: 'Regra removida', description: 'A regra foi excluída do modelo.' })
  }

  // ----- Submit principal -----
  async function onSubmit(values: FormValues) {
    if (!canSave) return
    if (rules.length === 0) {
      setRulesError('É preciso cadastrar pelo menos uma regra de comissionamento.')
      setActiveSection('rules')
      toast.error({
        title: 'Faltam dados',
        description: 'Cadastre pelo menos uma regra de comissionamento antes de salvar.',
      })
      return
    }
    try {
      const payload = {
        name: values.name,
        monthly_installments:    values.noInstallmentsLimit ? null : values.installments,
        monthly_plans_need_sold: values.noMeta              ? null : values.meta,
        status: values.status,
      }
      let savedId = id
      if (isEdit && id) {
        await cfg.service.update(id, payload as Partial<CommissionStructure>)
        // Cargo é da estrutura → propaga para todas as regras já persistidas.
        for (const r of rules) {
          if (r.id) await cfg.service.updateRule(id, r.id, { userType: values.cargo } as Partial<CommissionStructureRule>)
        }
      } else {
        const created = await cfg.service.create(payload as Partial<CommissionStructure>)
        savedId = created.id
        for (const r of rules) {
          await cfg.service.createRule(savedId, {
            installmentFrom: r.installmentFrom,
            installmentTo: r.installmentTo,
            installmentAction: r.installmentAction,
            value: r.value,
            commissionType: r.commissionType,
            userType: values.cargo,
          } as Omit<CommissionStructureRule, 'id' | 'commissionStructureId' | 'formatted'>)
        }
      }
      toast.success({
        title: isEdit ? 'Modelo atualizado' : 'Modelo criado',
        description: isEdit
          ? 'As alterações foram salvas com sucesso.'
          : 'O novo modelo de comissão foi criado.',
      })
      // REGRA GLOBAL: após criar/atualizar, sempre volta para a listagem.
      navigate(cfg.listPath)
    } catch (e) {
      applyApiErrorsToForm(e, form.setError, {
        monthly_installments: 'installments',
        monthly_plans_need_sold: 'meta',
      })
    }
  }

  // ----- Erros por seção (sidemenu) -----
  const basicErrorCount =
    (form.formState.errors.name ? 1 : 0) +
    (form.formState.errors.installments ? 1 : 0) +
    (form.formState.errors.meta ? 1 : 0)
  const rulesErrorCount = rulesError ? 1 : 0

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-gray-400" />
      </div>
    )
  }

  // -------------------- Seções --------------------
  const basicSection = (
    <SectionShell title="Dados do modelo" description="Configurações gerais da tabela de comissão.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Nome do modelo de comissão"
          required
          wrapperClassName="md:col-span-2"
          {...form.register('name')}
          error={form.formState.errors.name?.message}
        />

        <Controller
          name="cargo"
          control={form.control}
          render={({ field }) => (
            <Select
              label="Cargo"
              required
              wrapperClassName="md:col-span-2"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              options={CARGOS.map((c) => ({ value: c, label: CARGO_LABELS[c] }))}
              searchable={false}
              clearable={false}
              hint="Para qual cargo é esta tabela. As regras se aplicam a quem tiver este cargo na rede."
            />
          )}
        />

        <Controller
          name="installments"
          control={form.control}
          render={({ field, fieldState }) => (
            <FieldWithSwitch
              label="Por quantas parcelas?"
              required={!noInstallmentsLimit}
              disabled={noInstallmentsLimit}
              switchLabel="Sem meta"
              switchValue={noInstallmentsLimit}
              onSwitchChange={(v) => form.setValue('noInstallmentsLimit', v, { shouldValidate: true })}
            >
              <NumberInput
                value={field.value}
                onChange={field.onChange}
                disabled={noInstallmentsLimit}
                placeholder={noInstallmentsLimit ? '— Sem meta —' : '12'}
                min={1}
                max={60}
                error={fieldState.error?.message}
              />
            </FieldWithSwitch>
          )}
        />

        <Controller
          name="meta"
          control={form.control}
          render={({ field, fieldState }) => (
            <FieldWithSwitch
              label="Meta de planos vendidos mensalmente"
              required={!noMeta}
              disabled={noMeta}
              switchLabel="Sem meta"
              switchValue={noMeta}
              onSwitchChange={(v) => form.setValue('noMeta', v, { shouldValidate: true })}
            >
              <NumberInput
                value={field.value}
                onChange={field.onChange}
                disabled={noMeta}
                placeholder={noMeta ? '— Sem meta —' : '0'}
                min={0}
                error={fieldState.error?.message}
              />
            </FieldWithSwitch>
          )}
        />

        <Controller
          name="status"
          control={form.control}
          render={({ field }) => (
            <div className="md:col-span-2">
              <StatusToggle value={field.value} onChange={field.onChange} />
            </div>
          )}
        />
      </div>
    </SectionShell>
  )

  const rulesSection = (
    <SectionShell
      title="Regras de comissionamento"
      description="Defina o valor da comissão por faixa de parcela. É obrigatório cadastrar pelo menos uma regra."
    >
      {rulesError && (
        <div className="mb-4 px-3 py-2 rounded-md bg-rose-50 ring-1 ring-rose-200 text-[13px] text-rose-700">
          {rulesError}
        </div>
      )}

      {/* Linha de adição inline. NÃO usa <form> aqui (form aninhado dentro do
          form principal não é permitido pelo HTML e dispararia o submit pai).
          O submit é acionado manualmente pelo botão via ruleForm.handleSubmit. */}
      {canManageRules && (
      <div className="bg-gray-50 ring-1 ring-gray-200 rounded-md p-4 mb-4">
        <div className={cn('grid grid-cols-1 gap-3 items-start', ruleAction === 'A PARTIR DE' ? 'md:grid-cols-4' : 'md:grid-cols-5')}>
          <Controller
            name="installmentAction"
            control={ruleForm.control}
            render={({ field }) => (
              <Select
                label="Tipo"
                required
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                options={[
                  { value: 'INTERVALO',    label: 'Intervalo' },
                  { value: 'A PARTIR DE',  label: 'A partir de' },
                ]}
                searchable={false}
                clearable={false}
              />
            )}
          />
          <Controller
            name="installmentFrom"
            control={ruleForm.control}
            render={({ field, fieldState }) => (
              <NumberInput label="Parcela de" required value={field.value ?? null} onChange={field.onChange} min={1} max={60} error={fieldState.error?.message} />
            )}
          />
          {ruleAction !== 'A PARTIR DE' && (
            <Controller
              name="installmentTo"
              control={ruleForm.control}
              render={({ field, fieldState }) => (
                <NumberInput label="Parcela até" required value={field.value ?? null} onChange={field.onChange} min={1} max={60} error={fieldState.error?.message} />
              )}
            />
          )}
          <Controller
            name="commissionType"
            control={ruleForm.control}
            render={({ field }) => (
              <Select
                label="Tipo de comissão"
                required
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                options={[
                  { value: 'PORCENTAGEM', label: 'Porcentagem' },
                  { value: 'VALOR FIXO',  label: 'Valor fixo' },
                ]}
                searchable={false}
                clearable={false}
              />
            )}
          />
          <Controller
            name="value"
            control={ruleForm.control}
            render={({ field, fieldState }) =>
              ruleCommType === 'PORCENTAGEM'
                ? <PercentInput  label="Valor" required value={field.value ?? null} onChange={field.onChange} error={fieldState.error?.message} />
                : <CurrencyInput label="Valor" required value={field.value ?? null} onChange={field.onChange} error={fieldState.error?.message} />
            }
          />
        </div>
        <div className="mt-3 text-right">
          <Button
            type="button"
            onClick={ruleForm.handleSubmit(onAddRule, onValidationFail)}
            loading={ruleForm.formState.isSubmitting}
            iconLeft={<Plus size={12} />}
          >
            Adicionar regra
          </Button>
        </div>
      </div>
      )}

      {/* Listagem (ordenada por installmentFrom asc) */}
      <div className="overflow-x-auto bg-white rounded-md ring-1 ring-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left   text-[11px] font-semibold uppercase tracking-wider text-gray-500">Tipo</th>
              <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500">Faixa de parcelas</th>
              <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500">Tipo de comissão</th>
              <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500">Valor</th>
              <th className="px-2 w-12" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedRules.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">Nenhuma regra cadastrada.</td></tr>
            ) : sortedRules.map((r) => (
              <tr key={r.draftId}>
                <td className="px-3 py-2 text-xs text-gray-700">
                  {r.installmentAction === 'A PARTIR DE' ? 'A partir de' : 'Intervalo'}
                </td>
                <td className="px-3 py-2 text-xs text-center">
                  {r.installmentAction === 'A PARTIR DE'
                    ? `A partir da ${r.installmentFrom}ª`
                    : `${r.installmentFrom}ª – ${r.installmentTo}ª`}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500 text-center">{r.commissionType}</td>
                <td className="px-3 py-2 text-center">
                  {r.commissionType === 'PORCENTAGEM'
                    ? `${r.value.toFixed(2).replace('.', ',')}%`
                    : `R$ ${r.value.toFixed(2).replace('.', ',')}`}
                </td>
                <td className="px-2 py-2 text-right">
                  {canManageRules && (
                  <button
                    type="button"
                    onClick={() => setDeletingRule(r)}
                    aria-label="Remover regra"
                    className="cursor-pointer inline-flex items-center justify-center w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 transition"
                  >
                    <Trash2 size={14} />
                  </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionShell>
  )

  const sections: FormSection[] = [
    { key: 'basic', label: 'Dados do modelo',           icon: SettingsIcon, content: basicSection, hasError: basicErrorCount > 0, errorCount: basicErrorCount },
    { key: 'rules', label: 'Regras de comissionamento', icon: ListOrdered,  content: rulesSection, hasError: rulesErrorCount > 0, errorCount: rulesErrorCount },
  ]

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit, (errors) => {
        setActiveSection('basic')
        onValidationFail(errors)
      })}
    >
      <PageHeader
        title={isEdit ? 'Editar modelo de comissão' : 'Novo modelo de comissão'}
        subtitle={cfg.subtitle}
        crumbs={cfg.crumbs(isEdit)}
        backTo={cfg.listPath}
        actions={
          <Button
            type="submit"
            loading={form.formState.isSubmitting}
            iconLeft={<Save size={14} />}
            disabled={!canSave}
            title={canSave ? undefined : `Sem permissão para ${isEdit ? 'editar' : 'criar'} tabelas de comissão`}
          >
            Salvar
          </Button>
        }
      />

      <FormSidemenu
        sections={sections}
        activeKey={activeSection}
        onChange={setActiveSection}
      />

      <ConfirmDialog
        open={!!deletingRule}
        onClose={() => setDeletingRule(null)}
        onConfirm={confirmDelRule}
        title="Remover regra?"
        description={deletingRule
          ? `Remover regra ${deletingRule.installmentAction === 'A PARTIR DE'
              ? `'A partir da ${deletingRule.installmentFrom}ª'`
              : `'${deletingRule.installmentFrom}ª – ${deletingRule.installmentTo}ª'`}? Esta ação não pode ser desfeita.`
          : ''}
        confirmLabel="Remover"
        variant="danger"
      />
    </form>
  )
}

// =============================================================================
// Subcomponentes
// =============================================================================
function SectionShell({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  )
}

// Faixa numérica [from, to]. "A PARTIR DE" usa Infinity como to.
function rangeOf(r: { installmentAction: 'INTERVALO' | 'A PARTIR DE'; installmentFrom: number; installmentTo: number | null }) {
  return {
    from: r.installmentFrom,
    to: r.installmentAction === 'A PARTIR DE' ? Number.POSITIVE_INFINITY : (r.installmentTo ?? r.installmentFrom),
  }
}

/** Retorna a primeira regra existente que sobrepõe `draft`, ou null. */
function findOverlap(draft: DraftRule, existing: DraftRule[]): DraftRule | null {
  const a = rangeOf(draft)
  for (const r of existing) {
    if (r.draftId === draft.draftId) continue
    const b = rangeOf(r)
    if (a.from <= b.to && b.from <= a.to) return r
  }
  return null
}

function describeRangeInstallment(r: DraftRule): string {
  return r.installmentAction === 'A PARTIR DE'
    ? `a partir da ${r.installmentFrom}ª parcela`
    : `parcelas ${r.installmentFrom}ª a ${r.installmentTo}ª`
}

function FieldWithSwitch({
  label, required, switchLabel, switchValue, onSwitchChange, disabled, children,
}: {
  label: string
  required?: boolean
  switchLabel: string
  switchValue: boolean
  onSwitchChange: (v: boolean) => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-1">
        <label className={cn('text-xs font-medium', disabled ? 'text-gray-400' : 'text-gray-700')}>
          {label}
          {required && <span className="text-rose-500 ml-0.5">*</span>}
        </label>
        <Toggle checked={switchValue} onChange={onSwitchChange} label={switchLabel} size="sm" labelSize="sm" />
      </div>
      {children}
    </div>
  )
}
